export const DROPZZ_AUCTION_STATUS = {
  Draft: 0,
  Scheduled: 1,
  Live: 2,
  Ended: 3,
  Settled: 4,
  Cancelled: 5,
};

const DROPZZ_STATUS_KEYS = {
  [DROPZZ_AUCTION_STATUS.Draft]: 'admin.dropzz.statusDraft',
  [DROPZZ_AUCTION_STATUS.Scheduled]: 'admin.dropzz.statusScheduled',
  [DROPZZ_AUCTION_STATUS.Live]: 'admin.dropzz.statusLive',
  [DROPZZ_AUCTION_STATUS.Ended]: 'admin.dropzz.statusEnded',
  [DROPZZ_AUCTION_STATUS.Settled]: 'admin.dropzz.statusSettled',
  [DROPZZ_AUCTION_STATUS.Cancelled]: 'admin.dropzz.statusCancelled',
};

export const DROPZZ_DEPOSIT_STATUS = [
  {value: 'Pending', messageId: 'admin.dropzz.depositPending'},
  {value: 'Confirmed', messageId: 'admin.dropzz.depositConfirmed'},
  {value: 'Released', messageId: 'admin.dropzz.depositReleased'},
  {value: 'Forfeited', messageId: 'admin.dropzz.depositForfeited'},
];

const DROPZZ_DEPOSIT_STATUS_BY_CODE = {
  0: 'Pending',
  1: 'Confirmed',
  2: 'Released',
  3: 'Forfeited',
};

export const DROPZZ_VERIFICATION_STATUS = [
  {value: 0, messageId: 'admin.dropzz.verification.statusNone'},
  {value: 1, messageId: 'admin.dropzz.verification.statusPending'},
  {value: 2, messageId: 'admin.dropzz.verification.statusApproved'},
  {value: 3, messageId: 'admin.dropzz.verification.statusRejected'},
];

export const DROPZZ_DOCUMENT_TYPE = [
  {value: 0, messageId: 'admin.dropzz.verification.documentOther'},
  {value: 1, messageId: 'admin.dropzz.verification.documentProofOfFunds'},
  {value: 2, messageId: 'admin.dropzz.verification.documentPreApproval'},
];

const parseNamedEnum = (value, names, fallback = null) => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^-?\d+$/.test(trimmed)) {
      return Number(trimmed);
    }
    const mapped = names[trimmed.toLowerCase()];
    if (mapped !== undefined) {
      return mapped;
    }
  }
  return fallback;
};

export const parseDropzzVerificationStatus = (status) =>
  parseNamedEnum(
    status,
    {
      none: 0,
      pending: 1,
      approved: 2,
      rejected: 3,
    },
    0,
  );

export const isDropzzVerificationPending = (status) =>
  parseDropzzVerificationStatus(status) === 1;

export const parseDropzzDocumentType = (documentType) =>
  parseNamedEnum(
    documentType,
    {
      other: 0,
      proofoffunds: 1,
      proof_of_funds: 1,
      preapproval: 2,
      pre_approval: 2,
    },
    0,
  );

export const getDropzzVerificationStatusLabel = (status, messages) => {
  const match = DROPZZ_VERIFICATION_STATUS.find(
    (item) => item.value === parseDropzzVerificationStatus(status),
  );
  if (match && messages?.[match.messageId]) {
    return messages[match.messageId];
  }
  if (status === null || status === undefined || status === '') {
    return '';
  }
  return String(status);
};

export const getDropzzVerificationUserName = (record) => {
  const userName = record?.userName;
  if (userName !== null && userName !== undefined && String(userName).trim() !== '') {
    return String(userName).trim();
  }
  return record?.userId ?? '';
};

export const getDropzzDocumentTypeLabel = (documentType, messages) => {
  const match = DROPZZ_DOCUMENT_TYPE.find(
    (item) => item.value === parseDropzzDocumentType(documentType),
  );
  if (match && messages?.[match.messageId]) {
    return messages[match.messageId];
  }
  if (
    documentType === null ||
    documentType === undefined ||
    documentType === ''
  ) {
    return '';
  }
  return String(documentType);
};

export const ANTI_SNIPE_SECONDS = 0;
export const ANTI_SNIPE_EXTEND_BY_SECONDS = 0;

export const parseDropzzAuctionStatus = (status) =>
  parseNamedEnum(
    status,
    {
      draft: DROPZZ_AUCTION_STATUS.Draft,
      scheduled: DROPZZ_AUCTION_STATUS.Scheduled,
      upcoming: DROPZZ_AUCTION_STATUS.Scheduled,
      live: DROPZZ_AUCTION_STATUS.Live,
      ended: DROPZZ_AUCTION_STATUS.Ended,
      settled: DROPZZ_AUCTION_STATUS.Settled,
      cancelled: DROPZZ_AUCTION_STATUS.Cancelled,
      canceled: DROPZZ_AUCTION_STATUS.Cancelled,
    },
    null,
  );

export const canRecordDropzzOutcome = (status) => {
  const parsed = parseDropzzAuctionStatus(status);
  return (
    parsed === DROPZZ_AUCTION_STATUS.Ended ||
    parsed === DROPZZ_AUCTION_STATUS.Settled
  );
};

export const toDropzzDepositStatusName = (status) => {
  if (typeof status === 'number' && DROPZZ_DEPOSIT_STATUS_BY_CODE[status]) {
    return DROPZZ_DEPOSIT_STATUS_BY_CODE[status];
  }
  if (typeof status === 'string') {
    const trimmed = status.trim();
    if (
      /^-?\d+$/.test(trimmed) &&
      DROPZZ_DEPOSIT_STATUS_BY_CODE[Number(trimmed)]
    ) {
      return DROPZZ_DEPOSIT_STATUS_BY_CODE[Number(trimmed)];
    }
    const match = DROPZZ_DEPOSIT_STATUS.find(
      (item) => item.value.toLowerCase() === trimmed.toLowerCase(),
    );
    if (match) {
      return match.value;
    }
  }
  return null;
};

export const getDropzzDepositLabel = (status, messages) => {
  const name = toDropzzDepositStatusName(status);
  const match = DROPZZ_DEPOSIT_STATUS.find((item) => item.value === name);
  if (match && messages?.[match.messageId]) {
    return messages[match.messageId];
  }
  if (status === null || status === undefined || status === '') {
    return '';
  }
  return String(status);
};

export const hasDropzzWinner = (auction) => {
  if (auction?.outcome?.winnerUserId) {
    return true;
  }
  if (auction?.leadingBidId) {
    return true;
  }
  const amount = Number(auction?.leadingAmount ?? auction?.outcome?.winningAmount);
  return Number.isFinite(amount) && amount > 0;
};

export const canNotifyDropzzWinner = (auction) =>
  parseDropzzAuctionStatus(auction?.status) === DROPZZ_AUCTION_STATUS.Ended &&
  hasDropzzWinner(auction);

export const getDropzzLeadingLabel = (auction) =>
  auction?.leadingDisplayLabel || auction?.leadingBidderLabel || '';

export const getDropzzStatusLabel = (status, messages) => {
  const key = DROPZZ_STATUS_KEYS[parseDropzzAuctionStatus(status)];
  if (key && messages?.[key]) {
    return messages[key];
  }
  if (status === null || status === undefined || status === '') {
    return '';
  }
  return String(status);
};

export const getDropzzErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (data?.errors && typeof data.errors === 'object') {
    if (Array.isArray(data.errors)) {
      const joined = data.errors.filter(Boolean).join(' ');
      if (joined) {
        return joined;
      }
    } else {
      const messages = Object.values(data.errors).flatMap((msgs) =>
        Array.isArray(msgs) ? msgs : [msgs],
      );
      if (messages.length) {
        return messages.join(' ');
      }
    }
  }
  return data?.message || data?.msg || error?.message || fallback;
};

export const normalizeDropzzList = (responseData) => {
  const payload = responseData?.data ?? responseData;
  if (Array.isArray(payload)) {
    return {data: payload, total: payload.length};
  }
  const data =
    payload?.data ?? payload?.items ?? payload?.results ?? payload?.value ?? [];
  const total =
    payload?.total ??
    payload?.totalCount ??
    payload?.count ??
    (Array.isArray(data) ? data.length : 0);
  return {
    data: Array.isArray(data) ? data : [],
    total,
  };
};

export const normalizeDropzzItem = (responseData) => {
  const payload = responseData?.data ?? responseData;
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return payload.data &&
      typeof payload.data === 'object' &&
      !Array.isArray(payload.data)
      ? payload.data
      : payload;
  }
  return payload;
};

export const normalizeExclusivePropertyOptions = (responseData) => {
  const payload = responseData?.data ?? responseData;
  if (Array.isArray(payload)) {
    return payload;
  }
  const data =
    payload?.data ??
    payload?.value ??
    payload?.items ??
    payload?.results ??
    payload?.properties ??
    [];
  return Array.isArray(data) ? data : [];
};

export const getExclusivePropertyOptionLabel = (item) => {
  const address = [item?.address, item?.city, item?.state]
    .filter(Boolean)
    .join(', ');
  return address || item?.title || item?.id || '';
};

export const toUtcIsoString = (value) => {
  if (!value) {
    return null;
  }
  if (typeof value.toISOString === 'function') {
    return value.toISOString();
  }
  return value;
};
